import type {
  ConversationDetail,
  ConversationHandler,
  ConversationListItem,
  PostAgentMessageInput,
  ResolveConversationInput,
} from '@bruno/shared';
import { api } from './client';

export interface ListInboxParams {
  status?: 'open' | 'resolved' | 'all';
  handler?: ConversationHandler;
  assignee?: 'me' | 'unassigned' | 'any';
  websiteId?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export function listInboxConversations(
  params: ListInboxParams = {},
): Promise<{ conversations: ConversationListItem[] }> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.handler) search.set('handler', params.handler);
  if (params.assignee) search.set('assignee', params.assignee);
  if (params.websiteId) search.set('websiteId', params.websiteId);
  if (params.q) search.set('q', params.q);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  const qs = search.toString();
  return api<{ conversations: ConversationListItem[] }>(
    `/v1/inbox/conversations${qs ? `?${qs}` : ''}`,
  );
}

export function getInboxConversation(id: string): Promise<ConversationDetail> {
  return api<ConversationDetail>(`/v1/inbox/conversations/${id}`);
}

export function postAgentMessage(id: string, body: PostAgentMessageInput): Promise<{ message: ConversationDetail['messages'][number] }> {
  return api(`/v1/inbox/conversations/${id}/messages`, { method: 'POST', body });
}

export function handbackConversation(id: string): Promise<void> {
  return api<void>(`/v1/inbox/conversations/${id}/handback`, { method: 'POST' });
}

export function claimConversation(id: string): Promise<void> {
  return api<void>(`/v1/inbox/conversations/${id}/claim`, { method: 'POST' });
}

export interface ClaimPendingResponse {
  assignedAgentId: string;
  assignedAgentName: string;
  assignmentLockAt: string;
}

/** First-claim wins for `handler='queued'` conversations. The server
 *  returns 409 when another surface won the race; the dashboard
 *  surfaces that as a soft "already taken" toast rather than an error. */
export function claimPendingConversation(id: string): Promise<ClaimPendingResponse> {
  return api<ClaimPendingResponse>(`/v1/inbox/conversations/${id}/claim-pending`, {
    method: 'POST',
  });
}

export function resolveConversation(
  id: string,
  body: ResolveConversationInput = {},
): Promise<void> {
  return api<void>(`/v1/inbox/conversations/${id}/resolve`, { method: 'POST', body });
}

export function reopenConversation(id: string): Promise<void> {
  return api<void>(`/v1/inbox/conversations/${id}/reopen`, { method: 'POST' });
}
